import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useConfirmation } from '@/contexts/confirmation'
import { useModal } from '@/contexts/modal'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { AddCustomerJourneyNoteProps } from '@/modals/CustomerModals/AddCustomerJourneyNote'
import { CreateNegotiationProps } from '@/modals/NegotiationModals/CreateNegotiation'
import { ResponsePOCProps } from '@/modals/ServiceModals/ResponsePOC'
import { RetentionPOCProps } from '@/modals/ServiceModals/RetentionPOC'
import {
    convertPocServiceToOfficial,
    createContractNegotiation,
    createCustomerJourneyNote,
    deleteCustomerJourney,
    deletePlainNotesOnlyCustomerJourney,
    getContractNegotiationByCustomerJourneyId,
    getUnlinkedBusinesses,
    linkBusinessToJourney,
    markDealAsLost,
    reEngageCustomerJourney,
    UnlinkedBusiness,
    updateCustomerJourneyExplaination,
} from '@/modules/Customer/services'
import {
    ContractNegotiation,
    CustomerJourneyNote,
    Service,
} from '@/modules/Customer/types'
import usePermissionsStore from '@/stores/usePermissionsStore'
import { uniqBy } from '@/utils/function'
import { generatePathDatacentral, paths } from '@/utils/path'
import qs from 'query-string'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import JourneyInformationEdit from '../JourneyInformation/Edit'
import CollapsedJourneyCard from './CollapsedJourneyCard'
import { getJourneyOutcome } from './helpers'
import JourneyHeader from './JourneyHeader'
import StepColumn from './StepColumn'
import { SaleProcessBoardProps } from './types'
import { useCustomerReadOnly } from '../../CustomerReadOnlyContext'

const SaleProcessBoard = ({
    journey,
    journeySteps,
    customerId,
    contactId,
    journeyIndex,
    isUpsell,
}: SaleProcessBoardProps) => {
    const { t } = useTranslation()
    const { t: TService } = useTranslation('services')
    const { t: tCustomer } = useTranslation('customer')
    const { setModal } = useModal()
    const { setConfirmation } = useConfirmation()
    const { canDeletePlainNotesOnlyCustomerJourney } = usePermissionsStore()
    const isReadOnly = useCustomerReadOnly()
    const [isJourneyExpanded, setIsJourneyExpanded] = useState(false)
    const [showJourneyInfo, setShowJourneyInfo] = useState(false)
    const [pendingPocConvertServiceId, setPendingPocConvertServiceId] =
        useState<number | null>(null)

    // Filter only pipeline steps
    const pipelineSteps = useMemo(() => {
        return journeySteps.filter(
            (step) =>
                !['close_win', 'close_lose', 'terminate'].includes(step.value)
        )
    }, [journeySteps])

    // Mutations
    const { mutateAsync: createNoteAsync, isPending: isCreatingNote } =
        useMutationWithHandler(
            { mutationFn: createCustomerJourneyNote },
            {
                reactQueryKeys: [
                    QUERY_KEYS.CUSTOMERS,
                    QUERY_KEYS.CUSTOMERS_JOURNEYS,
                ],
            }
        )

    const { mutateAsync: markDealAsLostAsync } = useMutationWithHandler(
        { mutationFn: markDealAsLost },
        {
            reactQueryKeys: [
                QUERY_KEYS.CUSTOMERS,
                QUERY_KEYS.CUSTOMERS_JOURNEYS,
            ],
        }
    )

    const { mutateAsync: deleteJourneyAsync } = useMutationWithHandler(
        { mutationFn: deleteCustomerJourney },
        {
            reactQueryKeys: [
                QUERY_KEYS.CUSTOMERS,
                QUERY_KEYS.CUSTOMERS_JOURNEYS,
            ],
        }
    )

    const { mutateAsync: deletePlainNotesOnlyJourneyAsync } =
        useMutationWithHandler(
            { mutationFn: deletePlainNotesOnlyCustomerJourney },
            {
                reactQueryKeys: [
                    QUERY_KEYS.CUSTOMERS,
                    QUERY_KEYS.CUSTOMERS_JOURNEYS,
                ],
            }
        )

    const { mutateAsync: reEngageJourneyAsync } = useMutationWithHandler(
        { mutationFn: reEngageCustomerJourney },
        {
            reactQueryKeys: [
                QUERY_KEYS.CUSTOMERS,
                QUERY_KEYS.CUSTOMERS_JOURNEYS,
            ],
        }
    )

    const { mutateAsync: updateExplainationAsync } = useMutationWithHandler(
        { mutationFn: updateCustomerJourneyExplaination },
        {
            reactQueryKeys: [
                QUERY_KEYS.CUSTOMERS,
                QUERY_KEYS.CUSTOMERS_JOURNEYS,
            ],
        }
    )

    const { mutateAsync: convertPocAsync, isPending: isConvertingPoc } =
        useMutationWithHandler(
            { mutationFn: convertPocServiceToOfficial },
            {
                reactQueryKeys: [
                    QUERY_KEYS.CUSTOMERS,
                    QUERY_KEYS.CUSTOMERS_JOURNEYS,
                    QUERY_KEYS.SERVICES,
                ],
                successMessage: TService('convert-poc-to-service-success'),
            }
        )

    // Query for negotiations
    const { refetch: refetchNegotiations } = useQueryClientCallback({
        pattern: 'negotiations',
        mode: 'contains',
    })

    const { data: negotiationsData, refetch: refetchJourneyNegotiations } =
        useQueryWithHandler<{
            results: ContractNegotiation[]
        }>({
            queryKey: [
                QUERY_KEYS.CUSTOMERS_NEGOTIATIONS_LIST,
                journey.customer_journey_id,
            ],
            queryFn: () =>
                getContractNegotiationByCustomerJourneyId(
                    journey.customer_journey_id
                ),
            // Chế độ chỉ xem: không tải và không hiển thị thương lượng hợp đồng
            enabled: !isReadOnly,
        })

    const negotiations = isReadOnly ? [] : negotiationsData?.results || []

    const { mutateAsync: createNegotiationAsync } = useMutationWithHandler(
        { mutationFn: createContractNegotiation },
        {
            reactQueryKeys: [QUERY_KEYS.CUSTOMERS, QUERY_KEYS.NEGOTIATIONS],
            successMessage: t('negotiation-created-successfully'),
            onSuccess: () => {
                refetchJourneyNegotiations()
                refetchNegotiations()
            },
        }
    )

    // Query for unlinked businesses
    const { data: unlinkedBusinessesData, refetch: refetchUnlinkedBusinesses } =
        useQueryWithHandler<{ results: UnlinkedBusiness[] }>({
            queryKey: [QUERY_KEYS.CUSTOMERS, 'unlinked-businesses', customerId],
            queryFn: () => getUnlinkedBusinesses(customerId),
        })

    const unlinkedBusinesses = unlinkedBusinessesData?.results || []

    // Mutation for linking business to journey
    const { mutateAsync: linkBusinessAsync, isPending: isLinkingBusiness } =
        useMutationWithHandler(
            { mutationFn: linkBusinessToJourney },
            {
                reactQueryKeys: [
                    QUERY_KEYS.CUSTOMERS,
                    QUERY_KEYS.CUSTOMERS_JOURNEYS,
                ],
                successMessage: t('business-linked-successfully'),
                onSuccess: () => {
                    refetchUnlinkedBusinesses()
                },
            }
        )

    // Form control for inline service_interestings select
    const formControl = useForm({
        defaultValues: {
            service_interestings:
                journey.customer_journey_explaination?.service_interestings ||
                [],
        },
    })

    // Watch service_interestings changes and auto-save
    const watchedServiceInterestings = formControl.watch('service_interestings')
    const prevServiceInterestingsRef = useRef<string[]>(
        journey.customer_journey_explaination?.service_interestings || []
    )

    useEffect(() => {
        const prevValues = prevServiceInterestingsRef.current
        const currentValues = watchedServiceInterestings || []

        if (JSON.stringify(prevValues) === JSON.stringify(currentValues)) return

        prevServiceInterestingsRef.current = currentValues

        updateExplainationAsync({
            customer_id: customerId,
            customer_journey_id: journey.customer_journey_id,
            params: {
                ...journey.customer_journey_explaination,
                service_interestings: currentValues,
            },
        }).catch((error) => {
            console.error('Failed to update service_interestings:', error)
        })
    }, [watchedServiceInterestings])

    const outcome = getJourneyOutcome(journey)
    // readOnly dùng chung cờ khoá: header, step actions, POC card, chuyển bước
    const isLocked = journey.customer_journey_locked || isReadOnly
    const currentStepIndex = pipelineSteps.findIndex(
        (s) => s.value === journey.customer_journey_step
    )

    // Guard: require service_interestings before any journey action
    const hasServices =
        (journey.customer_journey_explaination?.service_interestings?.length ??
            0) > 0
    const requireServices = () => {
        if (!hasServices) {
            toast.error(tCustomer('fill-service-interestings-required'))
            return false
        }
        return true
    }

    const canUsePlainNotesOnlyDelete = () => {
        const notes = journey.customer_journey_notes || []
        const hasNoServiceReferrer =
            journey.service_referrer_id === null ||
            journey.service_referrer_id === undefined ||
            journey.service_referrer_id === -1

        return (
            canDeletePlainNotesOnlyCustomerJourney &&
            hasNoServiceReferrer &&
            notes.length > 0 &&
            notes.every(
                (note) =>
                    !note.reference_id &&
                    !note.reference_type &&
                    !note.attachment
            )
        )
    }

    // Get POC services from notes
    const pocServices = useMemo(() => {
        return (
            journey.customer_journey_notes?.filter(
                (note) =>
                    note.reference_type === 'service-poc' && note.service_poc
            ) || []
        )
    }, [journey.customer_journey_notes])

    // Get linked businesses from notes
    const linkedBusinesses = useMemo(() => {
        return (
            journey.customer_journey_notes?.filter(
                (note) => note.reference_type === 'business' && note.business
            ) || []
        )
    }, [journey.customer_journey_notes])

    // Get linked tickets from notes
    const linkedTickets = useMemo(() => {
        return (
            journey.customer_journey_notes?.filter(
                (note) => note.reference_type === 'ticket' && note.ticket
            ) || []
        )
    }, [journey.customer_journey_notes])

    // Get contract services from notes (unique by service_id)
    const contractServices = useMemo(() => {
        const services =
            journey.customer_journey_notes?.filter(
                (note) => note.reference_type === 'service' && note.service
            ) || []
        // @ts-ignore
        return uniqBy(services, (note) => note.service?.service_id)
    }, [journey.customer_journey_notes])

    // Check if contract already exists
    const hasContractCompleted = useMemo(() => {
        return journey.customer_journey_notes?.some(
            (note) =>
                note.customer_journey_note_step === 'contract' &&
                note.customer_journey_note_step_status === 'completed'
        )
    }, [journey.customer_journey_notes])

    // Helper to move to step if needed
    const moveToStepIfNeeded = async (targetStep: string) => {
        if (
            targetStep !== journey.customer_journey_step &&
            !isLocked &&
            !isCreatingNote
        ) {
            const stepLabel =
                pipelineSteps.find((s) => s.value === targetStep)?.label ||
                targetStep
            try {
                await createNoteAsync({
                    customer_id: customerId,
                    customer_journey_id: journey.customer_journey_id,
                    params: {
                        note: {
                            customer_journey_note: t('step-changed-to', {
                                step: stepLabel,
                            }),
                            customer_journey_note_step: targetStep,
                            customer_journey_note_step_status: 'processing',
                        },
                    },
                })
            } catch (error) {
                console.error('Failed to move step:', error)
            }
        }
    }

    // Handle move to step
    const handleMoveToStep = async (stepValue: string) => {
        if (isLocked || isCreatingNote) return
        if (stepValue === journey.customer_journey_step) return
        if (!requireServices()) return

        const stepLabel =
            pipelineSteps.find((s) => s.value === stepValue)?.label || stepValue

        try {
            await createNoteAsync({
                customer_id: customerId,
                customer_journey_id: journey.customer_journey_id,
                params: {
                    note: {
                        customer_journey_note: t('step-changed-to', {
                            step: stepLabel,
                        }),
                        customer_journey_note_step: stepValue,
                        customer_journey_note_step_status: 'processing',
                    },
                },
            })
        } catch (error) {
            console.error('Failed to move step:', error)
        }
    }

    // Handle add note
    const handleAddNote = async (stepValue: string) => {
        if (!requireServices()) return
        await moveToStepIfNeeded(stepValue)
        setModal<AddCustomerJourneyNoteProps>(
            'CustomerModals/AddCustomerJourneyNote/index',
            {
                customer_id: customerId,
                customer_journey_id: journey.customer_journey_id,
                contact_id: contactId,
                customer_journey_note_step_default: stepValue,
                customer_journey_note_step_status:
                    journey.customer_journey_step_status,
            }
        )
    }

    // Handle re-engage
    const handleReEngage = () => {
        setConfirmation({
            title: t('re-engage-customer'),
            description: t('re-engage-customer-description'),
            open: true,
            onConfirm: async () => {
                try {
                    await reEngageJourneyAsync({
                        customer_id: customerId,
                        customer_journey_id: journey.customer_journey_id,
                        note: t('re-engaged-from-closed'),
                    })
                } catch (error) {
                    console.error('Failed to re-engage:', error)
                }
            },
        })
    }

    // Handle mark as lost
    const handleMarkAsLost = () => {
        setConfirmation({
            title: t('mark-deal-as-on-hold'),
            description: t('mark-deal-on-hold-description'),
            open: true,
            useReason: true,
            onConfirm: async (reason?: string | null) => {
                try {
                    await markDealAsLostAsync({
                        customer_id: customerId,
                        customer_journey_id: journey.customer_journey_id,
                        reason: reason ?? '',
                    })
                } catch (error) {
                    console.error('Failed to mark as lost:', error)
                }
            },
        })
    }

    const handleDeleteJourney = () => {
        const shouldUsePlainNotesOnlyDelete = canUsePlainNotesOnlyDelete()

        setConfirmation({
            title: t('delete-journey'),
            description: t('delete-journey-description'),
            open: true,
            useReason: !shouldUsePlainNotesOnlyDelete,
            onConfirm: async (reason?: string | null) => {
                try {
                    if (shouldUsePlainNotesOnlyDelete) {
                        await deletePlainNotesOnlyJourneyAsync({
                            customer_id: customerId,
                            customer_journey_id: journey.customer_journey_id,
                        })
                    } else {
                        await deleteJourneyAsync({
                            customer_id: customerId,
                            customer_journey_id: journey.customer_journey_id,
                            reason: reason || '',
                        })
                    }
                } catch (error) {
                    console.error('Failed to delete journey:', error)
                }
            },
        })
    }

    // Get step status
    const getStepStatus = (stepIndex: number) => {
        if (outcome === 'won') return 'completed'
        if (outcome === 'lost' || outcome === 'terminated') return 'disabled'
        if (stepIndex < currentStepIndex) return 'completed'
        if (stepIndex === currentStepIndex) return 'current'
        return 'pending'
    }

    // Handle create POC
    const handleCreatePOC = async () => {
        if (!requireServices()) return
        await moveToStepIfNeeded('poc')
        const search = {
            customer_id: customerId,
            customer_journey_id: journey.customer_journey_id,
            contact_id: contactId,
        }
        window.open(
            `${generatePathDatacentral(paths.customerCreatePOC, {
                id: customerId,
            })}?${qs.stringify(search)}`,
            '_blank'
        )
    }

    // Handle create contract
    const handleCreateContract = async () => {
        if (!requireServices()) return
        await moveToStepIfNeeded('contract')
        const search = {
            customer_id: customerId,
            customer_journey_id: journey.customer_journey_id,
            contact_id: contactId,
        }
        window.open(`${paths.createContract}?${qs.stringify(search)}`, '_blank')
    }

    // Handle add negotiation
    const handleAddNegotiation = async (stepValue: string) => {
        if (!requireServices()) return
        setModal<CreateNegotiationProps>(
            'NegotiationModals/CreateNegotiation/index',
            {
                customerJourneyId: journey.customer_journey_id,
                customerId: Number(customerId),
                customerJourneyStep: stepValue,
                onSubmit: async (data: any) => {
                    await createNegotiationAsync({ data })
                },
            }
        )
    }

    // Handle update POC result
    const handleUpdatePOCResult = (note: CustomerJourneyNote) => {
        if (!note.service_poc) return
        setModal<ResponsePOCProps>('ServiceModals/ResponsePOC/index', {
            serviceID: Number(note.service_poc.service_id),
            service: {
                service_id: Number(note.service_poc.service_id),
                service_stopped_date: note.service_poc.service_stopped_date,
            } as Partial<Service>,
        })
    }

    // Handle POC retention
    const handlePOCRetention = (note: CustomerJourneyNote) => {
        if (!note.service_poc) return
        setModal<RetentionPOCProps>('ServiceModals/RetentionPOC/index', {
            serviceID: Number(note.service_poc.service_id),
            service: {
                service_id: Number(note.service_poc.service_id),
                service_stopped_date: note.service_poc.service_stopped_date,
            } as Partial<Service>,
        })
    }

    const handleConvertPOCToOfficial = (note: CustomerJourneyNote) => {
        if (!note.service_poc || isConvertingPoc) return
        const serviceId = Number(note.service_poc.service_id)
        setPendingPocConvertServiceId(serviceId)
        const serviceLabel = [
            note.service_poc.service_id,
            note.service_poc.service_name,
        ]
            .filter(Boolean)
            .join(' - ')

        setConfirmation({
            open: true,
            title: TService('convert-poc-to-service-confirm-title'),
            description: TService(
                'convert-poc-to-service-confirm-description',
                {
                    service: serviceLabel,
                }
            ),
            onConfirm: async () => {
                try {
                    await convertPocAsync({
                        customerID: customerId,
                        serviceID: serviceId,
                    })
                } finally {
                    setPendingPocConvertServiceId(null)
                }
            },
            onClose: () => {
                setPendingPocConvertServiceId(null)
            },
        })
    }

    // Handle link business
    const handleLinkBusiness = async (businessId: number) => {
        try {
            await linkBusinessAsync({
                businessId,
                journeyId: journey.customer_journey_id,
            })
        } catch (error) {
            console.error('Failed to link business:', error)
        }
    }

    // Handle create business trip
    const handleCreateBusinessTrip = async () => {
        if (!requireServices()) return
        await moveToStepIfNeeded('presentation')
        const search = {
            customer_id: customerId,
            customer_journey_id: journey.customer_journey_id,
        }
        window.open(
            `${paths.businessAccountCreate}?${qs.stringify(search)}`,
            '_blank'
        )
    }

    // Handle create meeting online
    const handleCreateMeetingOnline = async () => {
        if (!requireServices()) return
        await moveToStepIfNeeded('presentation')
        const search = {
            customer_id: customerId,
            customer_journey_id: journey.customer_journey_id,
            ticket_type: 'meeting',
            ticket_meeting_type: 'online',
        }
        window.open(`${paths.ticketCreate}?${qs.stringify(search)}`, '_blank')
    }

    const isCompleted =
        outcome === 'won' || outcome === 'lost' || outcome === 'terminated'

    // Collapsed view for completed journeys
    if (isCompleted && !isJourneyExpanded) {
        return (
            <CollapsedJourneyCard
                journey={journey}
                journeyIndex={journeyIndex}
                isUpsell={isUpsell}
                outcome={outcome}
                onExpand={() => setIsJourneyExpanded(true)}
            />
        )
    }

    return (
        <div
            className="bg-white rounded-md border border-gray-200 overflow-hidden transition-all"
            data-tour="sale-process-board"
        >
            {/* Journey Header */}
            <JourneyHeader
                journey={journey}
                journeyIndex={journeyIndex}
                isUpsell={isUpsell}
                outcome={outcome}
                isCompleted={isCompleted}
                isLocked={isLocked}
                showJourneyInfo={showJourneyInfo}
                onToggleJourneyInfo={() => setShowJourneyInfo(!showJourneyInfo)}
                onReEngage={handleReEngage}
                onMarkAsLost={handleMarkAsLost}
                onDeleteJourney={handleDeleteJourney}
                onToggleExpand={() => setIsJourneyExpanded(!isJourneyExpanded)}
                formControl={formControl}
            />

            {/* Journey Information Edit */}
            {showJourneyInfo && !isCompleted && (
                <div className="border-t border-gray-100">
                    <JourneyInformationEdit
                        selectedJourney={journey}
                        customerId={customerId}
                        contactId={contactId}
                        onClose={() => setShowJourneyInfo(false)}
                        onSave={() => setShowJourneyInfo(false)}
                    />
                </div>
            )}

            {/* Steps Grid */}
            {(!isCompleted || isJourneyExpanded) && (
                <div
                    className={`grid grid-cols-4 ${isCompleted ? 'border-t border-gray-100' : ''}`}
                >
                    {pipelineSteps.map((step, stepIndex) => {
                        const stepNegotiations = negotiations.filter(
                            (negotiation) =>
                                (negotiation.contract_negotiation_customer_journey_step ||
                                    'contract') === step.value
                        )

                        return (
                            <StepColumn
                                key={step.value}
                                step={step}
                                stepIndex={stepIndex}
                                status={getStepStatus(stepIndex)}
                                journey={journey}
                                customerId={customerId}
                                isLocked={isLocked}
                                isCompleted={isCompleted}
                                outcome={outcome}
                                linkedBusinesses={linkedBusinesses}
                                linkedTickets={linkedTickets}
                                unlinkedBusinesses={unlinkedBusinesses}
                                isLinkingBusiness={isLinkingBusiness}
                                pocServices={pocServices}
                                negotiations={stepNegotiations}
                                contractServices={contractServices}
                                hasContractCompleted={
                                    hasContractCompleted ?? false
                                }
                                onMoveToStep={handleMoveToStep}
                                onAddNote={handleAddNote}
                                onLinkBusiness={handleLinkBusiness}
                                onCreateBusinessTrip={handleCreateBusinessTrip}
                                onCreateMeetingOnline={
                                    handleCreateMeetingOnline
                                }
                                onCreatePOC={handleCreatePOC}
                                onAddNegotiation={handleAddNegotiation}
                                onCreateContract={handleCreateContract}
                                onUpdatePOCResult={handleUpdatePOCResult}
                                onPOCRetention={handlePOCRetention}
                                onConvertPOCToOfficial={
                                    handleConvertPOCToOfficial
                                }
                                pendingPocConvertServiceId={
                                    pendingPocConvertServiceId
                                }
                            />
                        )
                    })}
                </div>
            )}
        </div>
    )
}

export default SaleProcessBoard
